import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Search, Save, ExternalLink, RefreshCw, Image as ImageIcon, Type, Layout,
  Monitor, Tablet, Smartphone, ChevronDown, ChevronRight, Filter, Globe, X,
  Lock, LogOut, Wand2, Tag, Plus, Trash2, UploadCloud, Download, CheckCircle2, AlertTriangle, DownloadCloud,
  MapPin, Languages, Inbox, Mail, Images, Database, Library,
  ShieldCheck, Layers, Link2, Gauge, Gift, Coins, Eye, MessageSquareText,
} from "lucide-react";
import { ROUTES, pathFor } from "@/lib/routes";
import GalleryManager from "@/components/GalleryManager";
import TextSlotsPanel from "@/components/TextSlotsPanel";
import LibraryManager from "@/components/LibraryManager";
import ContestsPanel from "@/components/ContestsPanel";
import ProgramPricingPanel from "@/components/ProgramPricingPanel";
import FeedbackPanel from "@/components/FeedbackPanel";
import UnifiedLeadsPanel from "@/components/LeadsPanel";
import { Img } from "@/components/Img";
import { DEFAULT_PRICING, getFromPrice, fmtEuro } from "@/lib/pricing";
import { setPricingOverride } from "@/lib/pricingStore";
import { LANDMARK_CATALOG, infoSlots, cardSlots } from "@/lib/dayLandmarkCatalog";
import {
  ADMIN_TOKEN_KEY,
  adminAuthHeaders,
  notifyAdminSessionChanged,
} from "@/lib/adminSession";
import { useAdminSession } from "@/contexts/AdminSessionContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LANGS = ["es", "en", "fr"];
const DEVICES = [
  { id: "desktop", icon: Monitor,    label: "Desktop", w: "100%",  h: "100%" },
  { id: "tablet",  icon: Tablet,     label: "Tablet",  w: "820px", h: "1180px" },
  { id: "mobile",  icon: Smartphone, label: "Mobile",  w: "390px", h: "844px" },
];

/* ============================================================
   /admin URL taxonomy — full Xaluca Tours architecture grouped
   by business areas, navigation and travel regions. Region
   groups contain routes, and each route lists its programs
   grouped by duration. Single source: lib/routes ROUTES keys.
============================================================ */
const URL_TAXONOMY = [
  {
    group: "General",
    links: [
      ["Home", "home"],
      ["Marruecos", "morocco"],
      ["Qué ver en Marruecos", "whatToSee"],
      ["Cuándo viajar", "whenToTravel"],
      ["Galería", "galeria"],
      ["Blog", "blog"],
      ["Catálogo", "catalog"],
      ["Viajes (landing)", "toursLanding"],
    ],
  },
  {
    group: "Planificación del viaje",
    links: [
      ["Planifica tu viaje", "planTrip"],
      ["Cita previa", "appointment"],
      ["Precios", "precios"],
      ["Vuelos", "vuelos"],
      ["Próximas salidas", "upcomingDepartures"],
      ["Viajes a medida", "tourBespoke"],
    ],
  },
  {
    group: "Empresa",
    links: [
      ["Equipo", "about"],
      ["Qué hacemos", "whatWeDo"],
      ["Incentivos y eventos", "events"],
      ["Opiniones", "opiniones"],
      ["Contacto", "contact"],
      ["Feedback", "feedback"],
    ],
  },
  {
    group: "Viajes · Sur de Marruecos",
    region: true,
    routes: [
      { label: "Landing Sur de Marruecos", keys: ["tourSouth"] },
      { label: "Atlas → Desierto", keys: ["tourAtlasDesiertoHub", "tourAtlasDesierto45", "tourAtlasDesierto56", "tourAtlasDesierto67"] },
      { label: "Desierto → Atlas", keys: ["tourDesiertoAtlasHub", "tourDesiertoAtlas45", "tourDesiertoAtlas56", "tourDesiertoAtlas67"] },
      { label: "Marrakech → Erg Chebbi", keys: ["tourMarrakechErgHub", "tourMarrakechErg45", "tourMarrakechErg56", "tourMarrakechErg67", "tourMarrakechErg78"] },
      { label: "Erg Chebbi → Marrakech", keys: ["tourErgChebbiMarrakechHub", "tourErgMarrakech45", "tourErgMarrakech56", "tourErgMarrakech67", "tourErgMarrakech78"] },
      { label: "Marrakech → Erg Chebbi → Marrakech", keys: ["tourMarrakechLoopHub", "tourMarrakechLoop23", "tourMarrakechLoop34", "tourMarrakechLoop45", "tourMarrakechLoop56", "tourMarrakechLoop67", "tourMarrakechLoop78"] },
      { label: "Marrakech → Essaouira", keys: ["tourMarrakechEssHub", "tourMarrakechEss45", "tourMarrakechEss67"] },
      { label: "Errachidia → Atlas → Fez", keys: ["tourErrAtlasFezHub", "tourErrAtlasFez56"] },
      { label: "Fez → Atlas → Errachidia", keys: ["tourFezAtlasErr56"] },
    ],
  },
  {
    group: "Viajes · Gran Sur",
    region: true,
    routes: [
      { label: "Landing Gran Sur", keys: ["tourGransurFezRak", "tourGransurRakFezHub", "tourAtlasDesiertoFezHub", "tourGransurFezSidiali", "tourGransurOuarzaFez", "tourGransurTangerRak"] },
      { label: "Marrakech → Fez", keys: ["tourMarrakechFez67", "tourMarrakechFez78", "tourMarrakechFez89", "tourMarrakechFez910"] },
      { label: "Fez → Marrakech", keys: ["tourFezRak67", "tourFezRak78", "tourFezRak89", "tourFezRak910"] },
      { label: "Marrakech → Sidi Ali → Fez", keys: ["tourMarrakechSidialiFez78", "tourMarrakechSidialiFez89", "tourMarrakechSidialiFez910"] },
      { label: "Fez → Sidi Ali → Marrakech", keys: ["tourFezSidialiRak78", "tourFezSidialiRak89", "tourFezSidialiRak910"] },
      { label: "Fez → Sidi Ali → Ouarzazate", keys: ["tourFezSidialiOzz56", "tourFezSidialiOzz67", "tourFezSidialiOzz78"] },
      { label: "Ouarzazate → Sidi Ali → Fez", keys: ["tourOzzSidialiFez56", "tourOzzSidialiFez67", "tourOzzSidialiFez78"] },
      { label: "Tánger → Marrakech", keys: ["tourTangerRak89", "tourTangerRak910"] },
      { label: "Fin de Año", keys: ["tourFinDeAno2025"] },
      { label: "Marruecos Completo", keys: ["tourFull"] },
    ],
  },
  {
    group: "Viajes · Escapadas",
    region: true,
    routes: [
      { label: "Landing Escapadas", keys: ["tourShort"] },
      { label: "Desierto", keys: ["tourEscapadaDesierto34"] },
      { label: "Atlas", keys: ["tourEscapadaAtlas34"] },
      { label: "Fez", keys: ["tourEscapadaFez", "tourEscapadaFez23", "tourEscapadaFez34"] },
      { label: "Fez + Sidi Ali", keys: ["tourEscapadaFezSidiali34", "tourEscapadaFezSidiali45"] },
      { label: "Marrakech", keys: ["tourEscapadaMarrakech", "tourEscapadaMarrakech23"] },
      { label: "Marrakech + Agafay", keys: ["tourEscapadaRakAgafay34"] },
      { label: "Marrakech → Erg Chebbi → Marrakech", keys: ["tourEscapadaRakErgRakHub", "tourEscapadaRakErgRak23", "tourEscapadaRakErgRak34", "tourEscapadaRakErgRak45"] },
      { label: "Tánger", keys: ["tourEscapadaTanger"] },
    ],
  },
  {
    group: "Viajes · Norte de Marruecos",
    region: true,
    routes: [
      { label: "Landing Norte de Marruecos", keys: ["tourNorth"] },
      { label: "Ciudades Imperiales", keys: ["tourNorteCiudadesImperiales", "tourCiudadesImperiales45", "tourCiudadesImperiales67"] },
      { label: "Ciudades Imperiales + Rif", keys: ["tourCiudadesImperialesRif67", "tourCiudadesImperialesRif78"] },
      { label: "Tánger → Fez", keys: ["tourNorteTangerFez", "tourTangerFez45", "tourTangerFez56"] },
      { label: "Fez → Tánger", keys: ["tourFezTanger56", "tourFezTanger67"] },
    ],
  },
  {
    group: "Viajes · Aventura",
    region: true,
    routes: [
      { label: "Landing Aventura", keys: ["tourAdventure"] },
      { label: "Enduro", keys: ["tourAventuraEnduroHub", "tourEnduroAventura45", "tourEnduroAventura34"] },
    ],
  },
  {
    group: "Herramientas y páginas especiales",
    links: [
      ["Juego", "juego"],
      ["Próximas salidas", "upcomingDepartures"],
      ["Próximas salidas (tours)", "tourUpcoming"],
      ["Fin de Año", "tourFinDeAno2025"],
      ["Viajes a medida", "tourBespoke"],
    ],
  },
];

/* Duration badge from a route key suffix (e.g. 45 → 4N / 5D, 910 → 9N / 10D).
   Keys without a numeric suffix are landing pages / overviews. */
const durationBadge = (key) => {
  const m = key.match(/(\d+)$/);
  if (!m) return key.toLowerCase().includes("hub") ? "Resumen" : "Landing";
  const d = m[1];
  if (d.length >= 3) return `${d.slice(0, 1)}N / ${d.slice(1)}D`;
  return `${d[0]}N / ${d[1]}D`;
};


/* ============================================================
   /admin · centralised content dashboard
   ----------------------------------------------------------
   - URL list pulled from lib/routes (single source of truth).
   - Slot browser pulled from /api/slots (images) and
     /api/text_slots (texts), each editable inline + saved
     immediately to the same PUT endpoints used by the in-page
     CMS. Result: every existing EditableImage / EditableText
     across the site is mass-editable from one screen.
   - Live preview iframe re-renders on save (refresh trigger).
============================================================ */
export default function AdminPage() {
  const { refresh: refreshAdminSession } = useAdminSession();
  // ----- Admin password gate -----
  const [authed, setAuthed] = useState(null);   // null = checking, false = locked, true = ok
  const [pw, setPw] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    fetch(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setAuthBusy(true); setAuthErr("");
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      if (r.status === 401) { setAuthErr("Contraseña incorrecta"); setAuthBusy(false); return; }
      // Guard against a non-JSON response (e.g. the SPA index.html served with
      // 200 while the backend is briefly unavailable/restarting): avoids the
      // cryptic "Unexpected token '<'" JSON parse error and shows a clear msg.
      const ct = r.headers.get("content-type") || "";
      if (!r.ok || !ct.includes("application/json")) {
        setAuthErr("El servidor no está disponible ahora mismo. Espera unos segundos e inténtalo de nuevo.");
        setAuthBusy(false); return;
      }
      const d = await r.json();
      localStorage.setItem(ADMIN_TOKEN_KEY, d.token);
      notifyAdminSessionChanged();
      await refreshAdminSession();
      setAuthed(true);
    } catch {
      setAuthErr("Error de conexión");
    }
    setAuthBusy(false);
  };
  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    notifyAdminSessionChanged();
    setAuthed(false);
  };

  const [tab, setTab]            = useState("urls"); // urls | images | texts
  const [query, setQuery]        = useState("");
  const [selectedRoute, setRoute] = useState("home");
  const [previewLang, setLang]   = useState("es");
  const [device, setDevice]      = useState("desktop");
  const [imageSlots, setImageSlots] = useState([]);
  const [textSlots, setTextSlots]   = useState([]);
  const [loading, setLoading]    = useState(false);
  const [previewKey, bumpPreview] = useState(0);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => { document.title = "Admin · Xaluca CMS"; }, []);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const [imgRes, txtRes] = await Promise.all([
        fetch(`${API}/slots`).then((r) => r.json()).catch(() => ({})),
        fetch(`${API}/text_slots`).then((r) => r.json()).catch(() => ({})),
      ]);
      const imgArr = Array.isArray(imgRes) ? imgRes : (Array.isArray(imgRes?.slots) ? imgRes.slots : []);
      const ts = txtRes?.slots ?? txtRes;
      let txtArr = [];
      if (Array.isArray(ts)) txtArr = ts;
      else if (ts && typeof ts === "object") {
        txtArr = Object.entries(ts).map(([slot_id, v]) => ({
          slot_id,
          values: v && typeof v === "object" ? v : { es: v || "" },
        }));
      }
      setImageSlots(imgArr);
      setTextSlots(txtArr);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const previewSrc = useMemo(
    () => pathFor(previewLang, selectedRoute) + `?_admin=1#k=${previewKey}`,
    [previewLang, selectedRoute, previewKey]
  );
  const dev = DEVICES.find((d) => d.id === device);

  // ---- URLs grouped by namespace prefix (route key)
  const allRoutes = useMemo(() => Object.keys(ROUTES), []);

  const taxonomyView = useMemo(() => {
    const q = query.trim().toLowerCase();
    const used = new Set();
    const leafMatch = (key, label) =>
      !q ||
      key.toLowerCase().includes(q) ||
      (label || "").toLowerCase().includes(q) ||
      pathFor(previewLang, key).toLowerCase().includes(q);

    const groups = [];
    URL_TAXONOMY.forEach((g) => {
      if (g.links) {
        const links = g.links.filter(([, key]) => ROUTES[key]);
        links.forEach(([, key]) => used.add(key));
        const visible = links.filter(([label, key]) => leafMatch(key, label));
        if (visible.length) groups.push({ type: "links", group: g.group, links: visible });
      } else if (g.routes) {
        const routes = g.routes
          .map((rt) => {
            const keys = rt.keys.filter((k) => ROUTES[k]);
            keys.forEach((k) => used.add(k));
            return { label: rt.label, keys: keys.filter((k) => leafMatch(k, rt.label)) };
          })
          .filter((rt) => rt.keys.length);
        if (routes.length) groups.push({ type: "region", group: g.group, routes });
      }
    });

    // Catch-all: any ROUTES key not placed in the taxonomy above.
    const leftovers = allRoutes.filter((k) => !used.has(k) && leafMatch(k, k));
    if (leftovers.length) {
      groups.push({ type: "links", group: "Otras / sin clasificar", links: leftovers.map((k) => [k, k]) });
    }
    return groups;
  }, [query, previewLang, allRoutes]);

  const previewRouteGroups = useMemo(() => {
    const seen = new Set();
    const groups = URL_TAXONOMY.map((group) => {
      const options = [];
      (group.links || []).forEach(([label, key]) => {
        if (ROUTES[key] && !seen.has(key)) {
          seen.add(key);
          options.push({ key, label });
        }
      });
      (group.routes || []).forEach((route) => {
        route.keys.forEach((key) => {
          if (ROUTES[key] && !seen.has(key)) {
            seen.add(key);
            options.push({ key, label: `${route.label} · ${durationBadge(key)}` });
          }
        });
      });
      return { label: group.group, options };
    }).filter((group) => group.options.length);

    const remaining = allRoutes
      .filter((key) => !seen.has(key))
      .map((key) => ({ key, label: key }));
    if (remaining.length) groups.push({ label: "Otras páginas", options: remaining });
    return groups;
  }, [allRoutes]);

  // ---- Image / text slot grouping by id prefix (e.g. "home.cat.magic-south.image-0")
  const groupSlots = (slots) => {
    const m = {};
    slots.forEach((s) => {
      const top = (s.slot_id || s.id || "").split(".")[0] || "other";
      (m[top] ||= []).push(s);
    });
    return m;
  };
  const filteredImages = useMemo(
    () => imageSlots.filter((s) => (s.slot_id || "").toLowerCase().includes(query.toLowerCase())),
    [imageSlots, query]
  );
  const filteredTexts = useMemo(
    () => textSlots.filter((s) =>
      (s.slot_id || s.id || "").toLowerCase().includes(query.toLowerCase()) ||
      JSON.stringify(s.values || s.value || "").toLowerCase().includes(query.toLowerCase())
    ),
    [textSlots, query]
  );
  const imageGroups = useMemo(() => groupSlots(filteredImages), [filteredImages]);
  const textGroups  = useMemo(() => groupSlots(filteredTexts),  [filteredTexts]);

  const saveImage = async (slot_id, url) => {
    await fetch(`${API}/slots/${encodeURIComponent(slot_id)}`, {
      method: "PUT",
      headers: adminAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ url, source: "admin" }),
    });
    bumpPreview((k) => k + 1);
    fetchSlots();
  };
  const saveText = async (slot_id, values) => {
    await fetch(`${API}/text_slots/${encodeURIComponent(slot_id)}`, {
      method: "PUT",
      headers: adminAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ values }),
    });
    bumpPreview((k) => k + 1);
    fetchSlots();
  };

  // ----- One-click: fill THIS page's images from Pexels -----
  const iframeRef = useRef(null);
  const [fillBusy, setFillBusy] = useState(false);
  const [fillMsg, setFillMsg] = useState("");

  const buildQuery = (node) => {
    let base = (node.getAttribute("data-cms-alt") || "").trim();
    if (base.length < 4) {
      let el = node, heading = "";
      for (let i = 0; i < 6 && el; i++) {
        el = el.parentElement;
        if (!el) break;
        const h = el.querySelector && el.querySelector("h1,h2,h3,h4");
        if (h && h.textContent.trim().length > 3) { heading = h.textContent.trim(); break; }
      }
      base = heading || selectedRoute.replace(/([A-Z])/g, " $1").replace(/[._-]+/g, " ");
    }
    base = base.replace(/[•·|–—:]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
    const low = base.toLowerCase();
    if (!/(morocco|marruecos|maroc)/.test(low)) base += " Morocco";
    return base;
  };

  const fillPageImages = async () => {
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    if (!doc) { setFillMsg("No se pudo leer la página (recarga el preview)."); return; }
    const nodes = Array.from(doc.querySelectorAll("[data-cms-image-slot]"));
    const seen = new Set();
    const items = [];
    nodes.forEach((n) => {
      const slot_id = n.getAttribute("data-cms-image-slot");
      if (!slot_id || seen.has(slot_id)) return;
      seen.add(slot_id);
      const alt = (n.getAttribute("data-cms-alt") || "").trim();
      items.push({ slot_id, query: buildQuery(n), alt: alt || undefined });
    });
    if (!items.length) { setFillMsg("No hay imágenes detectables en esta página."); return; }
    setFillBusy(true);
    setFillMsg(`Rellenando ${items.length} imágenes desde Pexels…`);
    try {
      const r = await fetch(`${API}/pexels/bulk-fill`, {
        method: "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ items, orientation: "landscape", force: true }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      setFillMsg(`✓ ${d.ok}/${d.total} imágenes rellenadas${d.failed ? ` · ${d.failed} fallaron` : ""}.`);
      bumpPreview((k) => k + 1);
      fetchSlots();
    } catch {
      setFillMsg("Error al rellenar (la página puede tener demasiadas imágenes; reinténtalo).");
    }
    setFillBusy(false);
  };

  if (authed === null) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-[#0F0D0B] text-white/60 flex items-center justify-center text-sm tracking-[0.2em] uppercase">
        Cargando…
      </div>
    );
  }
  if (!authed) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#0F0D0B] text-[#FDFBF7] flex items-center justify-center px-6">
        <form onSubmit={doLogin} className="w-full max-w-sm bg-[#14110F] border border-white/10 p-8">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#D4A373] mb-6">
            <Lock className="w-4 h-4" strokeWidth={1.6} /> Xaluca · Admin
          </div>
          <h1 className="font-serif text-2xl mb-2">Acceso restringido</h1>
          <p className="text-sm text-white/55 mb-6">Introduce la contraseña para gestionar el contenido.</p>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Contraseña"
            autoFocus
            data-testid="admin-login-password"
            className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D4A373]"
          />
          {authErr && <p data-testid="admin-login-error" className="mt-3 text-xs text-[#E07856]">{authErr}</p>}
          <button
            type="submit"
            disabled={authBusy || !pw}
            data-testid="admin-login-submit"
            className={`mt-5 w-full py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors ${
              authBusy || !pw ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#C16542] hover:bg-[#A8533A] text-white"
            }`}
          >
            {authBusy ? "Comprobando…" : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0F0D0B] text-[#FDFBF7]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#14110F] border-b border-white/10 px-4 md:px-6 py-3 flex flex-wrap items-center gap-3 md:gap-4">
        <div className="w-full sm:w-auto flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#D4A373]">
          <Layout className="w-4 h-4" strokeWidth={1.6} />
          Xaluca · Admin
        </div>
        <div className="order-last sm:order-none basis-full sm:basis-0 sm:flex-1 min-w-0 max-w-xl sm:ml-4 flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2">
          <Search className="w-4 h-4 text-white/50" strokeWidth={1.8} />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar URL, slot, contenido…"
            className="bg-transparent outline-none flex-1 min-w-0 text-sm placeholder:text-white/40"
          />
          {query && <button onClick={() => setQuery("")} className="text-white/50 hover:text-white"><X className="w-3.5 h-3.5" /></button>}
        </div>
        <button
          data-testid="admin-refresh"
          onClick={fetchSlots}
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase border border-white/15 px-3 py-2 hover:bg-white/5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
          {loading ? "Loading…" : "Refresh"}
        </button>
        <a
          href={pathFor(previewLang, selectedRoute)}
          target="_blank" rel="noreferrer"
          className="xaluca-button inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase bg-[#C16542] hover:bg-[#A8533A] px-3 py-2"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} /> Open page
        </a>
        <button
          data-testid="admin-logout"
          onClick={logout}
          title="Cerrar sesión"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase border border-white/15 px-3 py-2 hover:bg-white/5"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.8} /> Salir
        </button>
      </header>

      {/* Body */}
      <div className="grid grid-cols-12 gap-0 min-h-[calc(100vh-56px)]">
        {/* Left rail */}
        <aside className="min-w-0 col-span-12 md:col-span-3 lg:col-span-2 border-r border-white/10 bg-[#14110F]">
          <nav className="p-3 flex md:flex-col gap-1 overflow-x-auto" aria-label="Secciones de administración">
            {[
              { id: "urls",   label: "URLs",          icon: Globe },
              { id: "livepreview", label: "Live Preview", icon: Eye },
              { id: "texts",  label: "Textos",        icon: Type },
              { id: "galleries", label: "Galerías",   icon: Images },
              { id: "library", label: "Library",     icon: Library },
              { id: "leads",  label: "Leads",         icon: Inbox },
              { id: "feedback", label: "Feedback",    icon: MessageSquareText },
              { id: "contests", label: "Concursos",   icon: Gift },
              { id: "notify", label: "Notificaciones", icon: Mail },
              { id: "programpricing", label: "Precios prog.", icon: Coins },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.22em] uppercase transition-colors ${
                    active ? "bg-[#C16542] text-white" : "text-white/75 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.6} /> {t.label}
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-white/10">
            <p className="text-[9px] tracking-[0.28em] uppercase text-white/40 mb-2 flex items-center gap-2">
              <Filter className="w-3 h-3" /> Preview lang
            </p>
            <div className="flex gap-1">
              {LANGS.map((l) => (
                <button
                  key={l}
                  data-testid={`admin-lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`flex-1 py-1.5 text-[10px] tracking-[0.22em] uppercase border ${
                    previewLang === l ? "bg-[#C16542] border-transparent" : "border-white/15 text-white/70 hover:bg-white/5"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>
        </aside>

        {/* Middle: lists */}
        {tab === "urls" && (
        <section className="col-span-12 md:col-span-4 lg:col-span-4 border-r border-white/10 overflow-y-auto max-h-[calc(100vh-56px)]">
          {tab === "urls" && (
            <div data-testid="admin-url-list" className="p-4 space-y-4">
              {taxonomyView.map((g) => {
                const collapsedG = collapsed[g.group];
                const count = g.type === "links" ? g.links.length : g.routes.reduce((n, r) => n + r.keys.length, 0);
                return (
                  <div key={g.group}>
                    <button
                      onClick={() => setCollapsed((c) => ({ ...c, [g.group]: !c[g.group] }))}
                      className="w-full flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#D4A373] mb-2"
                    >
                      {collapsedG ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {g.group} · {count}
                    </button>

                    {!collapsedG && g.type === "links" && (
                      <ul className="space-y-0.5">
                        {g.links.map(([label, r]) => (
                          <li key={r + label}>
                            <button
                              data-testid={`admin-route-${r}`}
                              onClick={() => setRoute(r)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                                selectedRoute === r ? "bg-[#C16542]/20 text-white" : "text-white/70 hover:bg-white/5"
                              }`}
                            >
                              <span className="truncate">{label}</span>
                              <span className="text-[10px] text-white/40 ml-2 truncate">{pathFor(previewLang, r)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {!collapsedG && g.type === "region" && (
                      <div className="space-y-1.5 pl-1.5 border-l border-white/10">
                        {g.routes.map((rt) => {
                          const subKey = `${g.group}::${rt.label}`;
                          const subCollapsed = collapsed[subKey];
                          return (
                            <div key={subKey}>
                              <button
                                onClick={() => setCollapsed((c) => ({ ...c, [subKey]: !c[subKey] }))}
                                className="w-full flex items-center gap-1.5 text-[11px] text-white/80 hover:text-white py-1"
                              >
                                {subCollapsed ? <ChevronRight className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />}
                                <span className="truncate text-left">{rt.label}</span>
                                <span className="text-[9px] text-white/30 ml-auto">{rt.keys.length}</span>
                              </button>
                              {!subCollapsed && (
                                <ul className="space-y-0.5 pl-4">
                                  {rt.keys.map((r) => (
                                    <li key={r}>
                                      <button
                                        data-testid={`admin-route-${r}`}
                                        onClick={() => setRoute(r)}
                                        className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 ${
                                          selectedRoute === r ? "bg-[#C16542]/20 text-white" : "text-white/65 hover:bg-white/5"
                                        }`}
                                      >
                                        <span className="shrink-0 inline-block min-w-[58px] text-[9px] tracking-wide px-1.5 py-0.5 bg-white/10 text-[#D4A373] text-center rounded-sm">
                                          {durationBadge(r)}
                                        </span>
                                        <span className="text-[10px] text-white/40 truncate">{pathFor(previewLang, r)}</span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </section>
        )}

        {/* Right: live preview */}
        {tab === "urls" ? (
        <section className="col-span-12 md:col-span-5 lg:col-span-6 bg-[#0F0D0B] flex flex-col">
          <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/60">
              <Layout className="w-3.5 h-3.5" /> Live preview · {selectedRoute}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {fillMsg && (
                <span data-testid="admin-fill-msg" className="text-[10px] tracking-[0.12em] text-white/70 max-w-[280px] truncate">{fillMsg}</span>
              )}
              <button
                data-testid="admin-fill-images"
                onClick={fillPageImages}
                disabled={fillBusy}
                title="Detecta los slots de imagen visibles en esta página y los rellena con fotos de Pexels"
                className={`inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors ${
                  fillBusy ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#3E7C59] hover:bg-[#326449] text-white"
                }`}
              >
                <Wand2 className={`w-3.5 h-3.5 ${fillBusy ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                {fillBusy ? "Rellenando…" : "Rellenar imágenes (Pexels)"}
              </button>
              <div className="flex items-center gap-1 bg-white/5 p-1">
                {DEVICES.map((d) => {
                  const Icon = d.icon;
                  return (
                    <button
                      key={d.id}
                      data-testid={`admin-device-${d.id}`}
                      onClick={() => setDevice(d.id)}
                      aria-label={d.label}
                      className={`p-2 transition-colors ${device === d.id ? "bg-[#C16542] text-white" : "text-white/60 hover:bg-white/10"}`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.7} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-[#1A1513] p-3 md:p-6 flex items-start justify-center">
            <div
              data-testid="admin-preview-frame"
              className="bg-white shadow-2xl transition-all duration-300"
              style={{ width: dev.w, maxWidth: "100%", height: dev.id === "desktop" ? "100%" : dev.h, minHeight: 600 }}
            >
              <iframe
                key={previewKey}
                ref={iframeRef}
                title="Live preview"
                src={previewSrc}
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </section>
        ) : tab === "livepreview" ? (
          <section
            className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] flex flex-col max-h-[calc(100vh-56px)]"
            data-testid="admin-live-preview"
          >
            <div className="border-b border-white/10 bg-[#14110F] px-4 py-3 space-y-3">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-[#D4A373]">
                    <Eye className="w-4 h-4" strokeWidth={1.7} />
                    <h2 className="text-sm tracking-[0.2em] uppercase">Live Preview</h2>
                  </div>
                  <p className="mt-1 text-xs text-white/45">
                    Revisa dentro del dashboard el resultado actual de cualquier página.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => bumpPreview((key) => key + 1)}
                    data-testid="live-preview-refresh"
                    className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-white/75 hover:bg-white/5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Actualizar
                  </button>
                  <a
                    href={pathFor(previewLang, selectedRoute)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="live-preview-open"
                    className="xaluca-button inline-flex items-center gap-2 bg-[#C16542] px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-white hover:bg-[#A8533A]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Abrir página
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <label className="min-w-[260px] flex-1 max-w-2xl">
                  <span className="sr-only">Página que se desea previsualizar</span>
                  <select
                    value={selectedRoute}
                    onChange={(event) => setRoute(event.target.value)}
                    data-testid="live-preview-route"
                    className="w-full bg-white/5 border border-white/15 px-3 py-2 text-xs text-white outline-none focus:border-[#D4A373]"
                  >
                    {previewRouteGroups.map((group) => (
                      <optgroup key={group.label} label={group.label} className="bg-[#14110F]">
                        {group.options.map((option) => (
                          <option key={option.key} value={option.key} className="bg-[#14110F]">
                            {option.label} · {pathFor(previewLang, option.key)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>

                <div className="flex items-center gap-1 bg-white/5 p-1" aria-label="Idioma de previsualización">
                  {LANGS.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setLang(lang)}
                      data-testid={`live-preview-lang-${lang}`}
                      className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase ${
                        previewLang === lang ? "bg-[#C16542] text-white" : "text-white/55 hover:bg-white/10"
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1 bg-white/5 p-1" aria-label="Dispositivo de previsualización">
                  {DEVICES.map((item) => {
                    const DeviceIcon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setDevice(item.id)}
                        aria-label={item.label}
                        title={item.label}
                        data-testid={`live-preview-device-${item.id}`}
                        className={`p-2 ${device === item.id ? "bg-[#C16542] text-white" : "text-white/55 hover:bg-white/10"}`}
                      >
                        <DeviceIcon className="w-4 h-4" strokeWidth={1.7} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-[#1A1513] p-3 md:p-5 flex items-start justify-center">
              <div
                className="bg-white shadow-2xl transition-[width,height] duration-300 overflow-hidden"
                style={{
                  width: dev.w,
                  maxWidth: "100%",
                  height: device === "desktop" ? "calc(100vh - 230px)" : dev.h,
                  minHeight: device === "desktop" ? 620 : undefined,
                }}
                data-testid="live-preview-frame"
              >
                <iframe
                  key={`live-${previewKey}-${selectedRoute}-${previewLang}`}
                  ref={iframeRef}
                  title={`Live preview · ${selectedRoute}`}
                  src={previewSrc}
                  className="w-full h-full border-0"
                  data-testid="live-preview-iframe"
                />
              </div>
            </div>
          </section>
        ) : tab === "leads" ? (
          <section className="min-w-0 col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] md:overflow-y-auto md:max-h-[calc(100vh-56px)]">
            <UnifiedLeadsPanel />
          </section>
        ) : tab === "feedback" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-hidden max-h-[calc(100vh-56px)]">
            <FeedbackPanel />
          </section>
        ) : tab === "contests" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <ContestsPanel />
          </section>
        ) : tab === "galleries" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-hidden max-h-[calc(100vh-56px)]">
            <GalleryManager lang={previewLang} />
          </section>
        ) : tab === "texts" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <TextSlotsPanel />
          </section>
        ) : tab === "library" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <LibraryManager />
          </section>
        ) : tab === "mirror" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <MirrorPanel />
            <ImageWeightPanel />
            <WarmCachePanel />
            <ReoptimizeLibraryPanel />
            <CorruptImagesPanel />
            <MigrateFallbacksPanel />
          </section>
        ) : tab === "programpricing" ? (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <ProgramPricingPanel />
          </section>
        ) : (
          <section className="col-span-12 md:col-span-9 lg:col-span-10 bg-[#0F0D0B] overflow-y-auto max-h-[calc(100vh-56px)]">
            <NotifyPanel />
          </section>
        )}
      </div>
    </div>
  );
}

/* ---------- Image editor row ---------- */
const ImageEditor = ({ slot, onSave }) => {
  const [url, setUrl] = useState(slot.url || "");
  const [busy, setBusy] = useState(false);
  const dirty = url !== (slot.url || "");
  const save = async () => {
    setBusy(true);
    try { await onSave(slot.slot_id, url); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`admin-image-${slot.slot_id}`} className="bg-white/[0.04] border border-white/10 p-3 flex gap-3">
      <div className="w-20 h-20 flex-shrink-0 bg-black/40 overflow-hidden border border-white/10">
        {url ? <Img src={url} alt={slot.alt || ""} width={160} sizes="80px" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-5 h-5" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 truncate" title={slot.slot_id}>{slot.slot_id}</p>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Image URL (https://…)"
          className="mt-2 w-full bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373]"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[9px] tracking-[0.22em] uppercase text-white/40">{slot.source || "external"}</span>
          <button
            onClick={save}
            disabled={!dirty || busy}
            data-testid={`admin-save-image-${slot.slot_id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase transition-colors ${
              dirty && !busy ? "bg-[#C16542] hover:bg-[#A8533A] text-white" : "bg-white/5 text-white/30 cursor-not-allowed"
            }`}
          >
            <Save className="w-3 h-3" strokeWidth={2} /> {busy ? "…" : "Save"}
          </button>
        </div>
      </div>
    </li>
  );
};

/* ---------- Text editor row ----------
   text_slots store either { value: "..." } (single string) or
   { values: { es, en, fr } } (i18n object). We support both. */
const TextEditor = ({ slot, onSave }) => {
  const isI18n = slot.values && typeof slot.values === "object";
  const [vals, setVals] = useState(isI18n
    ? { es: slot.values.es || "", en: slot.values.en || "", fr: slot.values.fr || "" }
    : { es: slot.value || "", en: "", fr: "" });
  const [busy, setBusy] = useState(false);
  const dirty = isI18n
    ? (vals.es !== (slot.values.es || "") || vals.en !== (slot.values.en || "") || vals.fr !== (slot.values.fr || ""))
    : vals.es !== (slot.value || "");

  const save = async () => {
    setBusy(true);
    try { await onSave(slot.slot_id || slot.id, vals); } finally { setBusy(false); }
  };

  return (
    <li data-testid={`admin-text-${slot.slot_id || slot.id}`} className="bg-white/[0.04] border border-white/10 p-3">
      <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 truncate" title={slot.slot_id || slot.id}>
        {slot.slot_id || slot.id}
      </p>
      <div className="mt-2 space-y-1.5">
        {LANGS.map((l) => (
          <div key={l} className="flex items-start gap-2">
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#D4A373] w-6 pt-1.5">{l}</span>
            <textarea
              value={vals[l]}
              onChange={(e) => setVals((v) => ({ ...v, [l]: e.target.value }))}
              rows={Math.max(1, Math.min(4, (vals[l] || "").length / 80 + 1))}
              className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373] resize-y"
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || busy}
          data-testid={`admin-save-text-${slot.slot_id || slot.id}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase transition-colors ${
            dirty && !busy ? "bg-[#C16542] hover:bg-[#A8533A] text-white" : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
        >
          <Save className="w-3 h-3" strokeWidth={2} /> {busy ? "…" : "Save"}
        </button>
      </div>
    </li>
  );
};

/* ---------- Global pricing editor ----------
   Edits the centralised price matrix (people × low/high) stored
   in the DB via PUT /api/pricing. Updates the live <FromPrice>/
   <PricingSection> store and reloads the preview on save. */
const PricingEditor = ({ onSaved }) => {
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetch(`${API}/pricing`)
      .then((r) => r.json())
      .then((d) => {
        const base =
          d && Array.isArray(d.tiers) && d.tiers.length ? d.tiers : DEFAULT_PRICING.tiers;
        setTiers(base.map((t) => ({ people: String(t.people), low: String(t.low), high: String(t.high) })));
      })
      .catch(() => setTiers(DEFAULT_PRICING.tiers.map((t) => ({ people: String(t.people), low: String(t.low), high: String(t.high) }))));
  }, []);

  if (!tiers) return <p className="text-sm text-white/50">Cargando precios…</p>;

  const onlyDigits = (v) => v.replace(/[^0-9]/g, "");
  const update = (i, key, val) =>
    setTiers((ts) => ts.map((t, idx) => (idx === i ? { ...t, [key]: onlyDigits(val) } : t)));
  const addTier = () =>
    setTiers((ts) => [...ts, { people: String((Number(ts[ts.length - 1]?.people) || 1) + 1), low: "0", high: "0" }]);
  const removeTier = (i) => setTiers((ts) => ts.filter((_, idx) => idx !== i));

  const nums = tiers.flatMap((t) => [Number(t.low), Number(t.high)]).filter((n) => n > 0);
  const fromPreview = nums.length ? Math.min(...nums) : 0;

  const save = async () => {
    setBusy(true);
    setMsg("");
    const token = localStorage.getItem("xaluca_admin_token");
    const payload = {
      currency: "EUR",
      tiers: tiers
        .map((t) => ({ people: Number(t.people) || 0, low: Number(t.low) || 0, high: Number(t.high) || 0 }))
        .filter((t) => t.people > 0),
    };
    try {
      const r = await fetch(`${API}/pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      setPricingOverride(d);
      setMsg("✓ Precios guardados. Se reflejan en toda la web.");
      onSaved && onSaved();
    } catch {
      setMsg("Error al guardar los precios.");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373] mb-1.5">
          Precios globales · Paquete 4x4
        </h3>
        <p className="text-xs text-white/55 leading-relaxed">
          Tarifa por persona según el número de viajeros. Estos precios se aplican a TODOS los
          viajes y a la etiqueta «Desde» de todas las cards. El mínimo se usa como «Desde».
        </p>
      </div>

      {/* Matrix header */}
      <div className="grid grid-cols-12 gap-2 text-[9px] tracking-[0.22em] uppercase text-white/45 px-1">
        <span className="col-span-4">Viajeros</span>
        <span className="col-span-3">Baja €</span>
        <span className="col-span-3">Alta €</span>
        <span className="col-span-2" />
      </div>

      {tiers.map((t, i) => (
        <div key={i} data-testid={`admin-pricing-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-4 flex items-center gap-2">
            <input
              value={t.people}
              onChange={(e) => update(i, "people", e.target.value)}
              data-testid={`admin-pricing-people-${i}`}
              className="w-14 bg-white/5 border border-white/10 px-2 py-1.5 text-sm text-white outline-none focus:border-[#D4A373]"
            />
            <span className="text-[10px] tracking-[0.18em] uppercase text-white/45">pers.</span>
          </div>
          <input
            value={t.low}
            onChange={(e) => update(i, "low", e.target.value)}
            data-testid={`admin-pricing-low-${i}`}
            className="col-span-3 bg-white/5 border border-white/10 px-2 py-1.5 text-sm text-white outline-none focus:border-[#D4A373]"
          />
          <input
            value={t.high}
            onChange={(e) => update(i, "high", e.target.value)}
            data-testid={`admin-pricing-high-${i}`}
            className="col-span-3 bg-white/5 border border-white/10 px-2 py-1.5 text-sm text-white outline-none focus:border-[#D4A373]"
          />
          <button
            onClick={() => removeTier(i)}
            data-testid={`admin-pricing-remove-${i}`}
            title="Eliminar tramo"
            className="col-span-2 inline-flex items-center justify-center py-1.5 text-white/40 hover:text-[#E07856] hover:bg-white/5"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
          </button>
        </div>
      ))}

      <button
        onClick={addTier}
        data-testid="admin-pricing-add"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-white/15 px-3 py-2 text-white/70 hover:bg-white/5"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={1.8} /> Añadir tramo
      </button>

      <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-4">
        <div className="text-xs text-white/60">
          <span className="block text-[9px] tracking-[0.22em] uppercase text-white/40">Desde (auto)</span>
          <span className="font-serif text-lg text-white" data-testid="admin-pricing-from">
            {fromPreview ? fmtEuro(fromPreview) : "—"}
          </span>
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="admin-pricing-save"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase transition-colors ${
            busy ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#C16542] hover:bg-[#A8533A] text-white"
          }`}
        >
          <Save className="w-3.5 h-3.5" strokeWidth={2} /> {busy ? "Guardando…" : "Guardar precios"}
        </button>
      </div>
      {msg && (
        <p data-testid="admin-pricing-msg" className="text-xs text-white/70">{msg}</p>
      )}
    </div>
  );
};


/* ---------- Mirror Production Database ----------
   One-click FULL pull: this environment's content is OVERWRITTEN with an
   exact copy of production (https://xalucatours.com). The backend reads
   production's public endpoints (cms/export, day-galleries, files) — no
   prod redeploy needed — and image binaries live in shared object storage,
   so previews show the same images immediately. Run on PREVIEW before a
   deploy so live CMS edits are never lost. */
const MirrorPanel = () => {
  const PROD = "https://xaluca-tours-api.onrender.com";
  const onProd = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") === PROD;
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runMirror = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const token = localStorage.getItem("xaluca_admin_token");
      const r = await fetch(`${API}/admin/mirror-production`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al sincronizar la base de datos.");
      setResult(d.mirrored || {});
    } catch (e) {
      setError(e.message || "Error al sincronizar la base de datos.");
    } finally {
      setBusy(false); setConfirming(false);
    }
  };

  const rows = result && [
    ["Textos y traducciones", result.text_slots],
    ["Imágenes (Edit Image)", result.image_slots],
    ["Galerías por día", result.day_galleries],
    ["Biblioteca de imágenes", result.files],
    ["Precios", result.pricing ? "Sí" : "—"],
  ];

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-10 text-white" data-testid="mirror-panel">
      <div className="flex items-center gap-3 mb-2">
        <Database className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
        <h2 className="text-2xl font-serif-x">Mirror Production Database</h2>
      </div>
      <p className="text-sm text-white/65 leading-relaxed mb-6">
        Copia el contenido completo de <span className="text-[#D4A373]">producción</span> a este entorno
        para que ambos queden idénticos antes de un despliegue. Así no se pierden los cambios hechos en la
        web en vivo (CMS, Edit Text, Edit Image). Las imágenes se comparten en el almacenamiento, así que
        aparecen al instante.
      </p>

      <div className="space-y-2 text-[11px] tracking-[0.15em] uppercase text-white/50 mb-6">
        <div className="flex items-center justify-between border border-white/10 px-4 py-2.5">
          <span>Origen (producción)</span>
          <span className="text-white/80 normal-case tracking-normal">{PROD}</span>
        </div>
        <div className="flex items-center justify-between border border-white/10 px-4 py-2.5">
          <span>Destino (este entorno)</span>
          <span className="text-white/80 normal-case tracking-normal truncate max-w-[55%]">
            {(process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "")}
          </span>
        </div>
      </div>

      {/* Destructive warning */}
      <div className="flex items-start gap-3 bg-[#C16542]/10 border border-[#C16542]/40 px-4 py-3 mb-6">
        <AlertTriangle className="w-4 h-4 text-[#C16542] mt-0.5 shrink-0" strokeWidth={1.8} />
        <p className="text-xs text-white/75 leading-relaxed">
          Esta acción <b>sobrescribe por completo</b> el contenido de este entorno (textos, imágenes,
          galerías, biblioteca y precios) con el de producción. No afecta a producción ni a los leads.
          {onProd && (
            <span className="block mt-2 text-[#C16542] font-semibold">
              ⚠ Estás en producción. Esta herramienta está pensada para ejecutarse en PREVIEW.
            </span>
          )}
        </p>
      </div>

      {!confirming ? (
        <button
          type="button"
          data-testid="mirror-start-btn"
          onClick={() => { setConfirming(true); setResult(null); setError(""); }}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
        >
          <DownloadCloud className="w-4 h-4" strokeWidth={1.8} />
          Mirror Production Database
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-3" data-testid="mirror-confirm-row">
          <span className="text-sm text-white/80">¿Sobrescribir este entorno con producción?</span>
          <button
            type="button"
            data-testid="mirror-confirm-btn"
            onClick={runMirror}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-white px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />}
            {busy ? "Sincronizando…" : "Sí, sobrescribir"}
          </button>
          <button
            type="button"
            data-testid="mirror-cancel-btn"
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-white disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      )}

      {busy && (
        <p className="mt-4 text-xs text-white/55">Copiando la base de datos de producción… puede tardar hasta un minuto. No cierres esta página.</p>
      )}

      {error && (
        <div className="mt-6 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="mirror-error">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
          <p className="text-xs text-red-300 leading-relaxed">{error}</p>
        </div>
      )}

      {result && (
        <div className="mt-6 border border-[#5A6B4F]/50 bg-[#5A6B4F]/10 px-5 py-4" data-testid="mirror-result">
          <div className="flex items-center gap-2 mb-3 text-[#9DBE8C]">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
            <span className="text-sm font-semibold">Mirror completado — este entorno es ahora una copia de producción.</span>
          </div>
          <ul className="space-y-1.5">
            {rows.map(([label, val]) => (
              <li key={label} className="flex items-center justify-between text-xs text-white/75">
                <span>{label}</span>
                <span className="text-white font-mono">{val}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-white/45">Recarga la web para ver el contenido actualizado.</p>
        </div>
      )}
    </div>
  );
};



/* ---------- Image cache warm-up ----------
   Pre-generates AVIF/WebP variants (+ the LQIP blur-up thumbnail) for EVERY
   stored image so the first ("cold") visitor also gets instant delivery — no
   initial blur on the most visible imagery. Runs automatically on the server
   at startup; this panel lets the editor re-run it after adding new images
   and watch progress. */
const ImageWeightPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const pollRef = useRef(null);
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });

  const fetchStats = async () => {
    try {
      const r = await fetch(`${API}/admin/image-cache/stats`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setStats(d);
      if (d?.warm && !d.warm.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    } catch (e) { /* transient */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchStats();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (stats?.warm?.running && !pollRef.current) {
      pollRef.current = setInterval(fetchStats, 2000);
    } else if (!stats?.warm?.running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
  }, [stats?.warm?.running]);

  const recompress = async () => {
    setBusy(true); setError(""); setConfirming(false);
    try {
      const r = await fetch(`${API}/admin/recompress-all`, { method: "POST", headers: tokenHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "No se pudo iniciar la re-compresión.");
      if (!pollRef.current) pollRef.current = setInterval(fetchStats, 2000);
      await fetchStats();
    } catch (e) {
      setError(e.message || "Error al iniciar."); setBusy(false);
    }
  };

  const m = stats?.masters;
  const v = stats?.variants;
  const s = stats?.settings;
  const warming = stats?.warm?.running || busy;
  const savings = (m?.avg_kb && v?.avg_avif_kb)
    ? Math.max(0, Math.round(100 - (v.avg_avif_kb / m.avg_kb) * 100)) : null;

  const Stat = ({ label, value, sub, testid }) => (
    <div className="bg-white/[0.03] border border-white/10 px-4 py-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45">{label}</div>
      <div className="mt-1 text-xl font-serif-x text-white">{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pb-10 text-white" data-testid="image-weight-panel">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Gauge className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Peso de las imágenes</h2>
          <button
            type="button"
            onClick={fetchStats}
            data-testid="image-weight-refresh-btn"
            className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white/55 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} /> Actualizar
          </button>
        </div>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Lo que el navegador descarga de verdad son las variantes <span className="text-[#D4A373]">AVIF/WebP</span>.
          Tras ajustar la calidad de compresión puedes <span className="text-[#D4A373]">re-comprimir todo</span> para
          regenerar las variantes ya cacheadas con los nuevos ajustes.
        </p>

        {stats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="image-weight-stats">
              <Stat label="Imágenes" value={m.count} sub={`${m.total_mb} MB en originales`} testid="iw-masters-count" />
              <Stat label="Peso medio AVIF" value={`${v.avg_avif_kb} KB`} sub={savings != null ? `~${savings}% más ligero que el original` : null} testid="iw-avg-avif" />
              <Stat label="Peso medio WebP" value={`${v.avg_webp_kb} KB`} testid="iw-avg-webp" />
              <Stat label="Variantes cacheadas" value={v.count} sub={`${v.avif_count} AVIF · ${v.webp_count} WebP`} testid="iw-variant-count" />
              <Stat label="Tamaño de caché" value={`${v.total_mb} MB`} testid="iw-cache-size" />
              <Stat label="Calidad" value={`AVIF q${s.avif_quality} · WebP q${s.webp_quality}`} sub={`warm: avif s${s.avif_speed_warm} · webp m${s.webp_method_warm}`} testid="iw-settings" />
            </div>

            <div className="mt-6 flex items-center gap-3">
              {!confirming ? (
                <button
                  type="button"
                  data-testid="recompress-all-btn"
                  onClick={() => setConfirming(true)}
                  disabled={warming}
                  className="inline-flex items-center gap-2 bg-[#5A6B4F] hover:bg-[#4A5A41] text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
                >
                  {warming ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <Gauge className="w-4 h-4" strokeWidth={1.8} />}
                  {warming ? "Re-comprimiendo…" : "Re-comprimir todo"}
                </button>
              ) : (
                <>
                  <span className="text-xs text-white/70">¿Vaciar caché y regenerar todas las variantes?</span>
                  <button type="button" data-testid="recompress-confirm-btn" onClick={recompress}
                    className="bg-[#C16542] hover:bg-[#a8512f] text-white px-4 py-2 text-[11px] tracking-[0.2em] uppercase transition-colors">Sí, re-comprimir</button>
                  <button type="button" data-testid="recompress-cancel-btn" onClick={() => setConfirming(false)}
                    className="text-white/55 hover:text-white px-3 py-2 text-[11px] tracking-[0.2em] uppercase transition-colors">Cancelar</button>
                </>
              )}
            </div>

            {warming && stats?.warm?.percent != null && (
              <div className="mt-5" data-testid="recompress-progress">
                <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
                  <div className="h-full bg-[#D4A373] transition-all duration-500" style={{ width: `${stats.warm.percent}%` }} />
                </div>
                <div className="mt-2 text-[11px] text-white/60">Regenerando variantes · {stats.warm.percent}%</div>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="image-weight-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const WarmCachePanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/admin/warm-image-cache/status`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setStatus(d);
      if (d && !d.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    } catch (e) { /* transient — keep polling */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Keep polling whenever a warm-up is running — whether it was started by the
  // button here OR automatically by the server at boot (button is disabled in
  // that case, so the click handler alone would never start the interval).
  useEffect(() => {
    if (status?.running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 1500);
    } else if (!status?.running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.running]);

  const startWarm = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/admin/warm-image-cache`, { method: "POST", headers: tokenHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "No se pudo iniciar la optimización.");
      setStatus(d);
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 1500);
    } catch (e) {
      setError(e.message || "Error al iniciar."); setBusy(false);
    }
  };

  const running = status?.running || busy;
  const pct = status?.percent ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pb-10 text-white" data-testid="warm-cache-panel">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Optimizar todas las imágenes</h2>
        </div>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Pre-genera las versiones modernas (AVIF/WebP) y la miniatura de previsualización (blur-up)
          de <span className="text-[#D4A373]">todas las imágenes</span>, para que incluso la primera
          visita cargue al instante, sin desenfoque inicial en las fotos más visibles. Se ejecuta solo
          al arrancar el servidor; vuelve a lanzarlo tras añadir imágenes nuevas.
        </p>

        <button
          type="button"
          data-testid="warm-cache-start-btn"
          onClick={startWarm}
          disabled={running}
          className="inline-flex items-center gap-2 bg-[#5A6B4F] hover:bg-[#4A5A41] text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />}
          {running ? "Optimizando…" : "Optimizar todas las imágenes"}
        </button>

        {status && (status.total > 0 || status.running) && (
          <div className="mt-6" data-testid="warm-cache-progress">
            <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
              <div className="h-full bg-[#D4A373] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/60">
              <span>{status.done}/{status.total} imágenes · {pct}%</span>
              <span>
                Generadas {status.generated} · en caché {status.skipped}
                {status.corrupt ? ` · dañadas ${status.corrupt}` : ""}
                {status.errors ? ` · errores ${status.errors}` : ""}
              </span>
            </div>
            {!status.running && status.corrupt > 0 && (
              <p className="mt-2 flex items-center gap-2 text-[11px] text-amber-300/90" data-testid="warm-cache-corrupt-note">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
                {status.corrupt} {status.corrupt === 1 ? "imagen dañada" : "imágenes dañadas"} (no son errores de la web: son archivos de origen corruptos). Límpialas abajo en «Imágenes dañadas».
              </p>
            )}
            {!status.running && status.total > 0 && (
              <p className="mt-3 flex items-center gap-2 text-xs text-[#9DBE8C]" data-testid="warm-cache-done">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} /> Optimización completada.
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="warm-cache-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Corrupt images cleanup panel ----------
   Finds (and optionally removes) master files that can't be decoded
   (corrupt/truncated source uploads). These are what surface as "dañadas"
   in the warm-up. Safe: re-verifies with retries before deleting. */
const CorruptImagesPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const pollRef = useRef(null);
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/admin/corrupt-images/status`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setStatus(d);
      if (d && !d.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    } catch (e) { /* transient — keep polling */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 1500);
    } else if (!status?.running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
  }, [status?.running]);

  const start = async (mode) => {
    setBusy(true); setError(""); setConfirming(false);
    const path = mode === "delete" ? "delete-corrupt-images" : "scan-corrupt-images";
    try {
      const r = await fetch(`${API}/admin/${path}`, { method: "POST", headers: tokenHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "No se pudo iniciar.");
      if (d.blocked_by) throw new Error(`Hay otro proceso en curso (${d.blocked_by}). Espera a que termine.`);
      setStatus((s) => ({ ...(s || {}), ...d }));
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 1500);
    } catch (e) {
      setError(e.message || "Error al iniciar."); setBusy(false);
    }
  };

  const running = status?.running || busy;
  const pct = status?.percent ?? 0;
  const items = status?.items || [];

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pb-10 text-white" data-testid="corrupt-images-panel">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="w-6 h-6 text-amber-400" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Imágenes dañadas</h2>
        </div>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Localiza archivos máster que están <span className="text-amber-300">corruptos o truncados</span> (subidas
          incompletas, descargas de stock fallidas). No se pueden convertir a AVIF/WebP, por eso aparecen como
          «dañadas» al optimizar. <span className="text-white/80">No afectan a la web</span> (se sirve su fallback),
          pero ensucian el contador. Primero <span className="text-[#D4A373]">busca</span> para ver cuáles son; luego
          <span className="text-[#D4A373]"> elimínalas</span> de forma segura (se re-verifica con reintentos antes de borrar).
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="corrupt-scan-btn"
            onClick={() => start("scan")}
            disabled={running}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
          >
            {running && status?.mode === "scan" ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <Search className="w-4 h-4" strokeWidth={1.8} />}
            {running && status?.mode === "scan" ? "Buscando…" : "Buscar imágenes dañadas"}
          </button>

          {!confirming ? (
            <button
              type="button"
              data-testid="corrupt-delete-btn"
              onClick={() => { setConfirming(true); setError(""); }}
              disabled={running}
              className="inline-flex items-center gap-2 bg-red-600/80 hover:bg-red-600 text-white px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.8} />
              {running && status?.mode === "delete" ? "Eliminando…" : "Eliminar imágenes dañadas"}
            </button>
          ) : (
            <div className="inline-flex items-center gap-2" data-testid="corrupt-delete-confirm-row">
              <span className="text-[11px] text-white/70">¿Eliminar las dañadas?</span>
              <button
                type="button"
                data-testid="corrupt-delete-confirm-btn"
                onClick={() => start("delete")}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.9} /> Sí, eliminar
              </button>
              <button
                type="button"
                data-testid="corrupt-delete-cancel-btn"
                onClick={() => setConfirming(false)}
                className="px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase text-white/70 hover:text-white transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>

        {status && (status.total > 0 || status.running) && (
          <div className="mt-6" data-testid="corrupt-progress">
            <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
              <div className="h-full bg-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/60">
              <span>{status.done}/{status.total} verificadas · {pct}%</span>
              <span data-testid="corrupt-counts">
                Dañadas {status.found ?? 0}{status.deleted ? ` · eliminadas ${status.deleted}` : ""}
              </span>
            </div>
            {!status.running && status.total > 0 && (
              <p className="mt-3 flex items-center gap-2 text-xs text-[#9DBE8C]" data-testid="corrupt-done">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
                {status.mode === "delete"
                  ? `Limpieza completada · ${status.deleted ?? 0} eliminada(s).`
                  : `Búsqueda completada · ${status.found ?? 0} dañada(s) encontrada(s).`}
              </p>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-4 border border-white/10 rounded-lg divide-y divide-white/5 max-h-56 overflow-auto" data-testid="corrupt-items">
            {items.map((it, i) => (
              <div key={it.path || i} className="px-4 py-2 flex items-center justify-between gap-3 text-[11px]">
                <span className="text-white/80 truncate">{it.filename || "(sin nombre)"}</span>
                <span className="text-white/40 shrink-0">{it.content_type || "?"} · {it.size ?? "?"} B</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="corrupt-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};


/* ---------- Re-optimize Library panel ----------
   One-click backfill that re-compresses oversized stored masters (old raw
   Pexels/Unsplash originals, 5–6 MB) down to bounded WebP masters in place. */
const ReoptimizeLibraryPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/admin/reoptimize-library/status`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setStatus(d);
      if (d && !d.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    } catch (e) { /* transient — keep polling */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 1500);
    } else if (!status?.running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
  }, [status?.running]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/admin/reoptimize-library`, { method: "POST", headers: tokenHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "No se pudo iniciar la re-optimización.");
      setStatus(d);
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 1500);
    } catch (e) {
      setError(e.message || "Error al iniciar."); setBusy(false);
    }
  };

  const running = status?.running || busy;
  const pct = status?.percent ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pb-10 text-white" data-testid="reoptimize-library-panel">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Re-optimizar Biblioteca</h2>
        </div>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Re-comprime los originales antiguos que aún pesan mucho (importados de
          Pexels/Unsplash antes de la optimización automática, de 5–6 MB) a un
          master WebP ligero (≤2000px), <span className="text-[#D4A373]">sobrescribiendo
          la misma ruta</span> para que ningún enlace se rompa. Libera almacenamiento
          y acelera la primera carga. Es seguro relanzarlo: omite lo ya optimizado.
        </p>

        <button
          type="button"
          data-testid="reoptimize-library-start-btn"
          onClick={start}
          disabled={running}
          className="inline-flex items-center gap-2 bg-[#5A6B4F] hover:bg-[#4A5A41] text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />}
          {running ? "Re-optimizando…" : "Re-optimizar Biblioteca"}
        </button>

        {status && (status.total > 0 || status.running) && (
          <div className="mt-6" data-testid="reoptimize-library-progress">
            <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
              <div className="h-full bg-[#D4A373] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/60">
              <span>{status.done}/{status.total} imágenes · {pct}%</span>
              <span>Optimizadas {status.optimized} · omitidas {status.skipped}{status.errors ? ` · errores ${status.errors}` : ""} · ahorrado {status.saved_mb ?? 0} MB</span>
            </div>
            {!status.running && status.total > 0 && (
              <p className="mt-3 flex items-center gap-2 text-xs text-[#9DBE8C]" data-testid="reoptimize-library-done">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} /> Re-optimización completada · {status.saved_mb ?? 0} MB liberados.
              </p>
            )}
          </div>
        )}

        {status && !status.running && status.total === 0 && status.done === 0 && status.finished_at && (
          <p className="mt-4 text-xs text-white/50" data-testid="reoptimize-library-empty">
            No hay masters grandes pendientes — la biblioteca ya está optimizada.
          </p>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="reoptimize-library-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};


/* ---------- Migrate fallbacks → CMS panel ----------
   One-click job that pulls every external (Unsplash/Pexels) code-fallback that
   is NOT yet saved in the CMS into our own storage and writes the resulting
   /api/files URL into image_slots. Centralises all images in the CMS. The code
   fallback stays as a never-triggered safety net until 100% coverage. */
const MigrateFallbacksPanel = () => {
  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [covLoading, setCovLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });

  const fetchCoverage = async () => {
    setCovLoading(true);
    try {
      const r = await fetch(`${API}/admin/migrate-fallbacks/coverage`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setCoverage(d);
    } catch (e) { /* transient */ }
    finally { setCovLoading(false); }
  };

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/admin/migrate-fallbacks/status`, { headers: tokenHeader() });
      const d = await r.json();
      if (r.ok) setStatus(d);
      if (d && !d.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
        fetchCoverage();
      }
    } catch (e) { /* transient — keep polling */ }
  };

  useEffect(() => {
    fetchStatus();
    fetchCoverage();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 1500);
    } else if (!status?.running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
  }, [status?.running]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/admin/migrate-fallbacks`, { method: "POST", headers: tokenHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "No se pudo iniciar la migración.");
      setStatus(d);
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 1500);
    } catch (e) {
      setError(e.message || "Error al iniciar."); setBusy(false);
    }
  };

  const running = status?.running || busy;
  const pct = status?.percent ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pb-10 text-white" data-testid="migrate-fallbacks-panel">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <DownloadCloud className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Centralizar imágenes en el CMS</h2>
        </div>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Importa a tu propio almacenamiento todas las imágenes que aún se
          sirven desde Unsplash/Pexels y <span className="text-[#D4A373]">no
          están guardadas en el CMS</span> — tanto las de slots editables como
          las imágenes directas (tarjetas, postales, pósters) — y las deja
          servidas desde tu CMS. Así nada se hotlinkea. Respeta lo que ya
          editaste, evita duplicados y es seguro relanzarlo.
          <br /><span className="text-white/45">Sugerencia: navega antes por todas las páginas (o deja pasar tráfico) para que el catálogo de imágenes esté completo.</span>
        </p>

        <button
          type="button"
          data-testid="migrate-fallbacks-start-btn"
          onClick={start}
          disabled={running}
          className="inline-flex items-center gap-2 bg-[#5A6B4F] hover:bg-[#4A5A41] text-white px-6 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} /> : <DownloadCloud className="w-4 h-4" strokeWidth={1.8} />}
          {running ? "Migrando…" : "Centralizar fallbacks en el CMS"}
        </button>

        {status && (status.total > 0 || status.running) && (
          <div className="mt-6" data-testid="migrate-fallbacks-progress">
            <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
              <div className="h-full bg-[#D4A373] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/60">
              <span>{status.done}/{status.total} slots · {pct}%</span>
              <span>Importadas {status.migrated} · reutilizadas {status.reused} · ya guardadas {status.skipped}{status.errors ? ` · errores ${status.errors}` : ""}</span>
            </div>
            {!status.running && status.total > 0 && (
              <p className="mt-3 flex items-center gap-2 text-xs text-[#9DBE8C]" data-testid="migrate-fallbacks-done">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} /> Migración completada · {status.migrated + status.reused} imágenes centralizadas en el CMS.
              </p>
            )}
          </div>
        )}

        {status && !status.running && status.total === 0 && status.finished_at && (
          <p className="mt-4 text-xs text-white/50" data-testid="migrate-fallbacks-empty">
            No hay fallbacks pendientes — todas las imágenes registradas ya están en el CMS.
          </p>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 px-4 py-3" data-testid="migrate-fallbacks-error">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-red-300 leading-relaxed">{error}</p>
          </div>
        )}

        <CoverageReport coverage={coverage} loading={covLoading} onRefresh={fetchCoverage} />
      </div>
    </div>
  );
};


/* ---------- Coverage report (Informe de cobertura) ----------
   Live snapshot from the DB of how many external (Unsplash/Pexels) image slots
   and direct <Img> URLs are already centralised in the CMS vs. still pending.
   When everything is done it is 100% safe to remove the code fallback system. */
const CovBar = ({ label, done, total, pending, accent = "#D4A373", testid }) => {
  const pct = total > 0 ? Math.round((done / total) * 1000) / 10 : 100;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-[11px] text-white/70 mb-1.5">
        <span className="tracking-[0.12em] uppercase">{label}</span>
        <span className="tabular-nums text-white/55">{done}/{total} · {pct}%</span>
      </div>
      <div className="h-2 w-full bg-white/10 overflow-hidden rounded-full">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
      {pending > 0 && (
        <p className="mt-1.5 text-[11px] text-[#C16542]">{pending} pendiente{pending === 1 ? "" : "s"} por centralizar</p>
      )}
    </div>
  );
};

const CoverageReport = ({ coverage, loading, onRefresh }) => {
  const c = coverage;
  const ov = c?.overall;
  const safe = !!ov?.safe_to_remove;
  return (
    <div className="mt-10 border-t border-white/10 pt-8" data-testid="coverage-report">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-[#D4A373]" strokeWidth={1.6} />
          <h2 className="text-2xl font-serif-x">Informe de cobertura</h2>
        </div>
        <button
          type="button"
          data-testid="coverage-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-white/80 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
          Actualizar
        </button>
      </div>
      <p className="text-sm text-white/60 leading-relaxed mb-6">
        Mide cuántas imágenes externas (Unsplash/Pexels) ya están centralizadas en tu CMS frente a las que aún faltan.
        Cuando llegue al <span className="text-[#D4A373]">100%</span> será totalmente seguro eliminar el sistema de fallback del código.
      </p>

      {!c && (
        <p className="text-xs text-white/45" data-testid="coverage-loading">
          {loading ? "Calculando cobertura…" : "Sin datos de cobertura todavía."}
        </p>
      )}

      {c && (
        <div className="space-y-6" data-testid="coverage-data">
          {/* Overall gauge */}
          <div
            className={`flex items-center gap-4 px-5 py-4 border ${safe ? "border-[#9DBE8C]/40 bg-[#5A6B4F]/15" : "border-[#D4A373]/30 bg-[#D4A373]/5"}`}
            data-testid="coverage-overall"
          >
            <div className={`text-4xl font-serif-x tabular-nums ${safe ? "text-[#9DBE8C]" : "text-[#D4A373]"}`} data-testid="coverage-overall-percent">
              {ov?.percent ?? 0}%
            </div>
            <div className="text-sm leading-snug">
              {safe ? (
                <span className="flex items-center gap-2 text-[#9DBE8C]" data-testid="coverage-safe-flag">
                  <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                  100% centralizado — es seguro eliminar el sistema de fallback del código.
                </span>
              ) : (
                <span className="flex items-center gap-2 text-white/70" data-testid="coverage-unsafe-flag">
                  <AlertTriangle className="w-4 h-4 text-[#C16542] shrink-0" strokeWidth={1.8} />
                  Aún quedan imágenes externas sin centralizar. No elimines los fallbacks todavía.
                </span>
              )}
              <p className="text-[11px] text-white/45 mt-1">{ov?.done ?? 0} de {ov?.total ?? 0} fuentes externas centralizadas.</p>
            </div>
          </div>

          {/* Breakdown bars */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white/80 text-xs tracking-[0.15em] uppercase">
                <Layers className="w-4 h-4 text-[#D4A373]" strokeWidth={1.8} /> Slots editables
              </div>
              <CovBar
                label="Slots con fallback externo"
                done={c.slots?.done ?? 0}
                total={c.slots?.external ?? 0}
                pending={c.slots?.pending ?? 0}
                testid="coverage-slots-bar"
              />
              <div className="text-[11px] text-white/45 leading-relaxed">
                {c.slots?.total ?? 0} slots registrados ·
                {" "}{c.slots?.migrated ?? 0} en el CMS ·
                {" "}{c.slots?.cleared ?? 0} vaciados ·
                {" "}{c.slots?.local_fallback ?? 0} ya locales ·
                {" "}{c.slots?.no_fallback ?? 0} sin fallback
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white/80 text-xs tracking-[0.15em] uppercase">
                <Link2 className="w-4 h-4 text-[#D4A373]" strokeWidth={1.8} /> Imágenes directas
              </div>
              <CovBar
                label="URLs <Img> remotas"
                done={c.urls?.migrated ?? 0}
                total={c.urls?.total ?? 0}
                pending={c.urls?.pending ?? 0}
                accent="#9DBE8C"
                testid="coverage-urls-bar"
              />
              <div className="text-[11px] text-white/45 leading-relaxed">
                {c.urls?.total ?? 0} URLs registradas ·
                {" "}{c.urls?.migrated ?? 0} migradas al CMS ·
                {" "}{c.urls?.pending ?? 0} pendientes
              </div>
            </div>
          </div>

          {/* Pending samples */}
          {(c.slots?.pending > 0 || c.urls?.pending > 0) && (
            <div className="border border-white/10 divide-y divide-white/5" data-testid="coverage-pending">
              {c.slots?.pending > 0 && (
                <div className="px-4 py-3">
                  <p className="text-[11px] tracking-[0.15em] uppercase text-[#C16542] mb-2">Slots pendientes (muestra de {Math.min(c.slots.pending, 30)} de {c.slots.pending})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(c.slots.pending_sample || []).map((sid) => (
                      <code key={sid} className="text-[10px] bg-white/5 text-white/60 px-2 py-0.5 rounded">{sid}</code>
                    ))}
                  </div>
                </div>
              )}
              {c.urls?.pending > 0 && (
                <div className="px-4 py-3">
                  <p className="text-[11px] tracking-[0.15em] uppercase text-[#C16542] mb-2">URLs pendientes (muestra de {Math.min(c.urls.pending, 30)} de {c.urls.pending})</p>
                  <ul className="space-y-0.5">
                    {(c.urls.pending_sample || []).map((u) => (
                      <li key={u} className="text-[10px] text-white/50 truncate">{u}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="px-4 py-2 text-[11px] text-white/45">Pulsa «Centralizar fallbacks en el CMS» arriba para importarlas. Antes, navega por las páginas para que el registro esté completo.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};






/* ---------- CMS Sync panel ----------
   Pushes all CMS content (image slots, text slots, global pricing) from the
   CURRENT backend (source = REACT_APP_BACKEND_URL) to a TARGET environment
   (e.g. production). Runs entirely in the browser:
     1. GET  {source}/api/cms/export
     2. POST {target}/api/admin/login  → token
     3. POST {target}/api/cms/import   (Bearer token)
   Also offers a one-click snapshot download for manual backups. */
const SyncPanel = () => {
  const [targetUrl, setTargetUrl] = useState(
    () => localStorage.getItem("xaluca_sync_target") || ""
  );
  const [password, setPassword] = useState("");
  const [wipe, setWipe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [counts, setCounts] = useState(null);
  const [verify, setVerify] = useState(null); // null | {status:'ok'|'warn', src, dst}

  const pushLog = (line) => setLog((l) => [...l, line]);
  const trimUrl = (u) => (u || "").trim().replace(/\/+$/, "");

  useEffect(() => {
    fetch(`${API}/cms/export`)
      .then((r) => r.json())
      .then((d) => setCounts(d.counts))
      .catch(() => setCounts(null));
  }, []);

  const downloadSnapshot = async () => {
    try {
      const r = await fetch(`${API}/cms/export`);
      const data = await r.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `xaluca_cms_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      pushLog("✗ No se pudo descargar el snapshot.");
    }
  };

  const publish = async () => {
    const to = trimUrl(targetUrl);
    if (!to) { pushLog("✗ Indica la URL del entorno destino."); return; }
    if (!password) { pushLog("✗ Indica la contraseña de admin del destino."); return; }
    if (to === trimUrl(process.env.REACT_APP_BACKEND_URL)) {
      pushLog("✗ El destino es el mismo entorno actual. Usa la URL de producción.");
      return;
    }
    localStorage.setItem("xaluca_sync_target", to);
    setBusy(true); setLog([]); setVerify(null);
    try {
      pushLog("→ Exportando contenido del entorno actual…");
      const exp = await fetch(`${API}/cms/export`).then((r) => r.json());
      pushLog(`  ✓ ${exp.counts.image_slots} imágenes + ${exp.counts.text_slots} textos.`);

      pushLog(`→ Iniciando sesión en ${to}…`);
      const lr = await fetch(`${to}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!lr.ok) throw new Error(`login destino falló (${lr.status}) — revisa la contraseña`);
      const { token } = await lr.json();
      if (!token) throw new Error("el destino no devolvió token");

      pushLog(`→ Importando en destino${wipe ? " (modo reemplazo total)" : ""}…`);
      const ir = await fetch(`${to}/api/cms/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          image_slots: exp.image_slots || [],
          text_slots: exp.text_slots || [],
          pricing: exp.pricing || null,
          wipe,
        }),
      });
      if (!ir.ok) {
        const t = await ir.text();
        throw new Error(`import falló (${ir.status}): ${t.slice(0, 160)}`);
      }
      const res = await ir.json();
      pushLog(`✓ Hecho. Importado: ${res.imported?.image_slots ?? res.image_slots ?? 0} imágenes, ${res.imported?.text_slots ?? res.text_slots ?? 0} textos, precios: ${(res.imported?.pricing ?? res.pricing) ? "sí" : "no"}.`);

      // ---- Post-publish verification: compare source ↔ destination counts ----
      pushLog("→ Verificando el destino…");
      const dst = await fetch(`${to}/api/cms/export`).then((r) => r.json());
      const src = exp.counts;
      const dstC = dst.counts;
      // wipe → exact mirror; upsert → destination must contain at least the source slots
      const ok = wipe
        ? dstC.image_slots === src.image_slots && dstC.text_slots === src.text_slots
        : dstC.image_slots >= src.image_slots && dstC.text_slots >= src.text_slots;
      setVerify({ status: ok ? "ok" : "warn", src, dst: dstC, wipe });
      if (ok) {
        pushLog(`✓ Verificación correcta: destino con ${dstC.image_slots} imágenes y ${dstC.text_slots} textos.`);
      } else {
        pushLog(`⚠ Discrepancia: destino ${dstC.image_slots}/${dstC.text_slots} vs origen ${src.image_slots}/${src.text_slots}.`);
      }
      pushLog("Tip: recarga el sitio destino (Ctrl/Cmd+Shift+R) para ver los cambios.");
    } catch (e) {
      pushLog(`✗ ${e.message}`);
    }
    setBusy(false);
  };

  // ---- Reverse sync: pull content FROM production INTO this (preview) env ----
  const pullFromProd = async () => {
    const from = trimUrl(targetUrl);
    if (!from) { pushLog("✗ Indica la URL de producción en el campo de arriba."); return; }
    if (from === trimUrl(process.env.REACT_APP_BACKEND_URL)) {
      pushLog("✗ El origen es el mismo entorno actual. Usa la URL de producción.");
      return;
    }
    const token = localStorage.getItem("xaluca_admin_token");
    if (!token) { pushLog("✗ Sesión admin no válida. Vuelve a iniciar sesión."); return; }
    localStorage.setItem("xaluca_sync_target", from);
    setBusy(true); setLog([]); setVerify(null);
    try {
      pushLog(`→ Exportando contenido de producción (${from})…`);
      const exp = await fetch(`${from}/api/cms/export`).then((r) => r.json());
      pushLog(`  ✓ ${exp.counts.image_slots} imágenes + ${exp.counts.text_slots} textos.`);

      pushLog("→ Importando en este entorno (preview)…");
      const ir = await fetch(`${API}/cms/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          image_slots: exp.image_slots || [],
          text_slots: exp.text_slots || [],
          pricing: exp.pricing || null,
          wipe,
        }),
      });
      if (!ir.ok) {
        const t = await ir.text();
        throw new Error(`import falló (${ir.status}): ${t.slice(0, 160)}`);
      }
      const res = await ir.json();
      pushLog(`✓ Hecho. Importado: ${res.imported?.image_slots ?? 0} imágenes, ${res.imported?.text_slots ?? 0} textos, precios: ${res.imported?.pricing ? "sí" : "no"}.`);

      pushLog("→ Verificando preview…");
      const dst = await fetch(`${API}/cms/export`).then((r) => r.json());
      const src = exp.counts;
      const dstC = dst.counts;
      const ok = wipe
        ? dstC.image_slots === src.image_slots && dstC.text_slots === src.text_slots
        : dstC.image_slots >= src.image_slots && dstC.text_slots >= src.text_slots;
      setVerify({ status: ok ? "ok" : "warn", src, dst: dstC, wipe });
      pushLog(ok
        ? `✓ Preview igualado: ${dstC.image_slots} imágenes y ${dstC.text_slots} textos.`
        : `⚠ Discrepancia: preview ${dstC.image_slots}/${dstC.text_slots} vs producción ${src.image_slots}/${src.text_slots}.`);
      pushLog("Tip: recarga esta web (Ctrl/Cmd+Shift+R) para ver los cambios.");
      setCounts(dstC);
    } catch (e) {
      pushLog(`✗ ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373] mb-1.5">
          Sincronizar contenido CMS
        </h3>
        <p className="text-xs text-white/55 leading-relaxed">
          Publica todas tus ediciones (imágenes, textos y precios) de este entorno a producción
          con un clic. {counts && (
            <span className="text-white/75">
              Snapshot actual: {counts.image_slots} imágenes · {counts.text_slots} textos.
            </span>
          )}
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">
            URL del entorno destino (producción)
          </label>
          <input
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://tu-sitio.onrender.com"
            data-testid="admin-sync-target"
            className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#D4A373]"
          />
        </div>
        <div>
          <label className="block text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">
            Contraseña de admin del destino
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            data-testid="admin-sync-password"
            className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#D4A373]"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-white/65 cursor-pointer">
          <input
            type="checkbox"
            checked={wipe}
            onChange={(e) => setWipe(e.target.checked)}
            data-testid="admin-sync-wipe"
            className="accent-[#C16542]"
          />
          Reemplazo total (borra el contenido del destino antes de importar)
        </label>
        <p className="text-[11px] text-white/40 leading-relaxed">
          La contraseña solo es necesaria para <span className="text-white/60">Publicar</span> (escribe en producción).
          «Traer de producción» solo lee de producción e importa aquí (usa tu sesión actual).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
        <button
          onClick={publish}
          disabled={busy}
          data-testid="admin-sync-publish"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase transition-colors ${
            busy ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#C16542] hover:bg-[#A8533A] text-white"
          }`}
        >
          <UploadCloud className={`w-3.5 h-3.5 ${busy ? "animate-pulse" : ""}`} strokeWidth={1.8} />
          {busy ? "Publicando…" : "Publicar en producción"}
        </button>
        <button
          onClick={pullFromProd}
          disabled={busy}
          data-testid="admin-sync-pull"
          title="Trae el contenido de producción e iguala este entorno (preview)"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase transition-colors ${
            busy ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#3E6B8A] hover:bg-[#335874] text-white"
          }`}
        >
          <DownloadCloud className={`w-3.5 h-3.5 ${busy ? "animate-pulse" : ""}`} strokeWidth={1.8} />
          {busy ? "Trayendo…" : "Traer de producción"}
        </button>
        <button
          onClick={downloadSnapshot}
          disabled={busy}
          data-testid="admin-sync-download"
          title="Descargar copia JSON del contenido actual"
          className="inline-flex items-center gap-2 px-3 py-2.5 text-[10px] tracking-[0.22em] uppercase border border-white/15 text-white/70 hover:bg-white/5"
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Snapshot
        </button>
      </div>

      {verify && (
        <div
          data-testid="admin-sync-verify"
          data-status={verify.status}
          className={`flex items-start gap-3 border p-3 ${
            verify.status === "ok"
              ? "border-[#3E7C59]/50 bg-[#3E7C59]/10"
              : "border-[#C9871F]/50 bg-[#C9871F]/10"
          }`}
        >
          {verify.status === "ok"
            ? <CheckCircle2 className="w-5 h-5 text-[#7BB98A] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
            : <AlertTriangle className="w-5 h-5 text-[#E0B25A] flex-shrink-0 mt-0.5" strokeWidth={1.8} />}
          <div className="min-w-0">
            <p className={`text-[11px] tracking-[0.18em] uppercase font-medium ${verify.status === "ok" ? "text-[#7BB98A]" : "text-[#E0B25A]"}`}>
              {verify.status === "ok" ? "✓ Producción sincronizada" : "⚠ Revisar sincronización"}
            </p>
            <p className="mt-1 text-xs text-white/70 leading-relaxed">
              Origen: {verify.src.image_slots} imágenes · {verify.src.text_slots} textos.{" "}
              Destino: {verify.dst.image_slots} imágenes · {verify.dst.text_slots} textos.
              {verify.status === "ok"
                ? " La migración se completó al 100%."
                : " Los conteos no coinciden; revisa el log y reintenta."}
            </p>
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div
          data-testid="admin-sync-log"
          className="bg-black/40 border border-white/10 p-3 text-[11px] leading-relaxed text-white/80 font-mono space-y-0.5 max-h-64 overflow-y-auto"
        >
          {log.map((line, i) => (
            <div key={i} className={line.startsWith("✗") ? "text-[#E07856]" : line.startsWith("✓") ? "text-[#7BB98A]" : ""}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ============================================================
   PoiManager · centralised "Puntos destacados" editor
   ------------------------------------------------------------
   Lists every point of interest used across the day maps and
   itineraries (deduped by stable poiKey). For each POI you edit
   its MAIN card — Título, Descripción, Imagen principal — which
   writes the GLOBAL slots `poi.${poiKey}.gallery.0[.title|.desc]`.
   Those are the exact slots the LandmarkCarousel / route galleries
   render, so a single edit syncs the POI everywhere it appears.
============================================================ */
const KIND_LABEL = {
  town: "Ciudad", village: "Aldea", kasbah: "Kasbah", site: "Sitio",
  palm: "Palmeral", gorges: "Gargantas", valley: "Valle", mountain: "Montaña",
  dunes: "Dunas", viewpoint: "Mirador", market: "Mercado", music: "Música",
  fossils: "Fósiles", hotel: "Hotel", camp: "Campamento", airport: "Aeropuerto",
};

const PoiManager = ({ query, imageSlots, textSlots, onSaveImage, onSaveText, onChanged }) => {
  const imageMap = useMemo(() => {
    const m = {};
    (imageSlots || []).forEach((s) => { if (s.slot_id) m[s.slot_id] = s; });
    return m;
  }, [imageSlots]);
  const textMap = useMemo(() => {
    const m = {};
    (textSlots || []).forEach((s) => {
      const id = s.slot_id || s.id;
      if (id) m[id] = s.values || (s.value ? { es: s.value } : {});
    });
    return m;
  }, [textSlots]);

  const q = (query || "").trim().toLowerCase();
  const list = useMemo(
    () => LANDMARK_CATALOG.filter((p) =>
      !q ||
      p.id.toLowerCase().includes(q) ||
      (p.group || "").toLowerCase().includes(q) ||
      Object.values(p.name || {}).some((v) => (v || "").toLowerCase().includes(q))
    ),
    [q]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373] mb-1.5 flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> Puntos destacados · {list.length}/{LANDMARK_CATALOG.length}
        </h3>
        <p className="text-xs text-white/55 leading-relaxed">
          Cada punto del <span className="text-white/75">Mapa del día</span> (de todos los itinerarios) es un bloque independiente.
          Al desplegarlo verás su información y todas las <span className="text-white/75">cards de la Galería del lugar</span> (ES/EN/FR + imagen).
          Al guardar, el cambio se sincroniza automáticamente en el mapa del día y en todas las galerías donde aparezca ese mismo punto.
        </p>
      </div>
      {list.length === 0 && (
        <p className="text-sm text-white/50">Ningún punto coincide con la búsqueda.</p>
      )}
      <ul className="space-y-3">
        {list.map((poi) => (
          <PoiRow
            key={poi.uid}
            poi={poi}
            imageMap={imageMap}
            textMap={textMap}
            onSaveImage={onSaveImage}
            onSaveText={onSaveText}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </div>
  );
};

const triEqual = (a, b) =>
  (a.es || "") === (b.es || "") && (a.en || "") === (b.en || "") && (a.fr || "") === (b.fr || "");

/* A text override only "counts" if it actually carries content (an empty
   {} tombstone left after a delete must not flag the POI as edited). */
const hasVals = (v) => !!(v && (v.es || v.en || v.fr));

const PoiRow = ({ poi, imageMap, textMap, onSaveImage, onSaveText, onChanged }) => {
  const [open, setOpen] = useState(false);
  const info = infoSlots(poi.prefix);
  const card0 = cardSlots(poi.prefix, 0);
  const headTitle = (poi.hasInfo && textMap[info.name]?.es) || poi.name?.es || poi.id;
  const headImg = imageMap[card0.image]?.url || poi.cards[0]?.image || "";
  const overridden =
    (poi.hasInfo && (hasVals(textMap[info.name]) || hasVals(textMap[info.blurb]))) ||
    poi.cards.some((_, i) => {
      const s = cardSlots(poi.prefix, i);
      return !!imageMap[s.image]?.url || hasVals(textMap[s.title]) || hasVals(textMap[s.desc]);
    });

  return (
    <li data-testid={`admin-poi-${poi.uid}`} className="bg-white/[0.04] border border-white/10">
      {/* Block header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`admin-poi-toggle-${poi.uid}`}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/[0.03]"
      >
        <div className="w-14 h-14 flex-shrink-0 bg-black/40 overflow-hidden border border-white/10">
          {headImg
            ? <Img src={headImg} alt="" width={120} sizes="56px" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-4 h-4" /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white truncate flex items-center gap-2">
            {headTitle}
            {overridden && <span className="text-[8px] tracking-[0.18em] uppercase bg-[#3E7C59]/30 text-[#7BB98A] px-1.5 py-0.5">editado</span>}
          </p>
          <p className="text-[10px] tracking-[0.14em] uppercase text-white/40 truncate">
            {KIND_LABEL[poi.kind] || poi.kind} · {poi.cards.length} cards · {poi.group}
          </p>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-white/10 pt-3 space-y-3">
          {/* Location name + description (curated landmarks only) */}
          {poi.hasInfo && (
            <LandmarkInfoEditor
              poi={poi}
              slots={info}
              textMap={textMap}
              onSaveText={onSaveText}
            />
          )}
          {/* The GALERÍA DEL LUGAR cards */}
          <p className="text-[9px] tracking-[0.24em] uppercase text-white/35 pt-1">Galería del lugar · {poi.cards.length} cards</p>
          {poi.cards.map((card, i) => (
            <PoiCardEditor
              key={i}
              poiKey={poi.uid}
              index={i}
              slots={cardSlots(poi.prefix, i)}
              defaults={card}
              imageMap={imageMap}
              textMap={textMap}
              onSaveImage={onSaveImage}
              onSaveText={onSaveText}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </li>
  );
};

/* Editable name + description of a curated day-map location (its own info,
   shown on the map's side list). Writes ${prefix}.name / ${prefix}.blurb. */
const LandmarkInfoEditor = ({ poi, slots, textMap, onSaveText }) => {
  const nameOv = textMap[slots.name];
  const blurbOv = textMap[slots.blurb];
  const initName = {
    es: nameOv?.es ?? poi.name.es ?? "", en: nameOv?.en ?? poi.name.en ?? "", fr: nameOv?.fr ?? poi.name.fr ?? "",
  };
  const initBlurb = {
    es: blurbOv?.es ?? poi.blurb.es ?? "", en: blurbOv?.en ?? poi.blurb.en ?? "", fr: blurbOv?.fr ?? poi.blurb.fr ?? "",
  };
  const [name, setName] = useState(initName);
  const [blurb, setBlurb] = useState(initBlurb);
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [msg, setMsg] = useState("");

  const dirty = !triEqual(name, initName) || !triEqual(blurb, initBlurb);

  const autoTranslate = async () => {
    setTranslating(true); setMsg("");
    try {
      const doOne = async (text) => {
        if (!text || !text.trim()) return {};
      const r = await fetch(`${API}/translate`, {
          method: "POST", headers: adminAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ text, source: "es", targets: ["en", "fr"] }),
        });
        if (!r.ok) throw new Error(String(r.status));
        return (await r.json()).translations || {};
      };
      const [tn, tb] = await Promise.all([doOne(name.es), doOne(blurb.es)]);
      setName((v) => ({ ...v, en: tn.en || v.en, fr: tn.fr || v.fr }));
      setBlurb((v) => ({ ...v, en: tb.en || v.en, fr: tb.fr || v.fr }));
      setMsg("✓ Traducido desde ES (revisa y guarda).");
    } catch { setMsg("✗ La traducción automática falló."); }
    setTranslating(false);
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const tasks = [];
      if (!triEqual(name, initName)) tasks.push(onSaveText(slots.name, name));
      if (!triEqual(blurb, initBlurb)) tasks.push(onSaveText(slots.blurb, blurb));
      await Promise.all(tasks);
      setMsg("✓ Guardado y sincronizado.");
    } catch { setMsg("✗ Error al guardar."); }
    setBusy(false);
  };

  return (
    <div data-testid={`admin-poi-info-${poi.uid}`} className="bg-black/20 border border-white/10 p-3 space-y-3">
      <p className="text-[9px] tracking-[0.24em] uppercase text-[#D4A373]">Localización · nombre y descripción</p>
      <div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">Nombre</p>
        <div className="space-y-1.5">
          {LANGS.map((l) => (
            <div key={l} className="flex items-center gap-2">
              <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 w-6">{l}</span>
              <input
                value={name[l]}
                onChange={(e) => setName((v) => ({ ...v, [l]: e.target.value }))}
                data-testid={`admin-poi-name-${poi.uid}-${l}`}
                className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373]"
              />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">Descripción</p>
        <div className="space-y-1.5">
          {LANGS.map((l) => (
            <div key={l} className="flex items-start gap-2">
              <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 w-6 pt-1.5">{l}</span>
              <textarea
                value={blurb[l]}
                onChange={(e) => setBlurb((v) => ({ ...v, [l]: e.target.value }))}
                rows={2}
                data-testid={`admin-poi-blurb-${poi.uid}-${l}`}
                className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373] resize-y"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={autoTranslate}
          disabled={translating || !name.es}
          data-testid={`admin-poi-info-translate-${poi.uid}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-white/70 hover:bg-white/5 disabled:opacity-40"
        >
          <Languages className={`w-3.5 h-3.5 ${translating ? "animate-pulse" : ""}`} strokeWidth={1.8} />
          {translating ? "Traduciendo…" : "Auto-traducir"}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || busy}
          data-testid={`admin-poi-info-save-${poi.uid}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
            dirty && !busy ? "bg-[#C16542] hover:bg-[#A8533A] text-white" : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
        >
          <Save className="w-3 h-3" strokeWidth={2} /> {busy ? "Guardando…" : "Guardar"}
        </button>
      </div>
      {msg && (
        <p data-testid={`admin-poi-info-msg-${poi.uid}`} className={`text-[11px] ${msg.startsWith("✗") ? "text-[#E07856]" : "text-[#7BB98A]"}`}>{msg}</p>
      )}
    </div>
  );
};

/* One editable card of a location's "Galería del lugar".
   Writes the global slots given in `slots` (${prefix}.gallery.${i}…). */
const PoiCardEditor = ({ poiKey, index, slots, defaults, imageMap, textMap, onSaveImage, onSaveText, onChanged }) => {
  const imageOverride = imageMap[slots.image]?.url;
  const titleOverride = textMap[slots.title];
  const descOverride = textMap[slots.desc];

  const initImg = imageOverride || defaults.image || "";
  const initTitle = {
    es: titleOverride?.es ?? defaults.title.es ?? "",
    en: titleOverride?.en ?? defaults.title.en ?? "",
    fr: titleOverride?.fr ?? defaults.title.fr ?? "",
  };
  const initDesc = {
    es: descOverride?.es ?? defaults.desc.es ?? "",
    en: descOverride?.en ?? defaults.desc.en ?? "",
    fr: descOverride?.fr ?? defaults.desc.fr ?? "",
  };

  const [imgUrl, setImgUrl] = useState(initImg);
  const [title, setTitle] = useState(initTitle);
  const [desc, setDesc] = useState(initDesc);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const imgDirty = imgUrl !== initImg;
  const titleDirty = !triEqual(title, initTitle);
  const descDirty = !triEqual(desc, initDesc);
  const dirty = imgDirty || titleDirty || descDirty;
  const overridden = !!imageOverride || hasVals(titleOverride) || hasVals(descOverride);

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/slots/${encodeURIComponent(slots.image)}/upload`, {
        method: "POST",
        headers: adminAuthHeaders(),
        body: fd,
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      if (d.url) setImgUrl(d.url);
      setMsg("✓ Imagen subida.");
      onChanged && onChanged();
    } catch {
      setMsg("✗ Error al subir la imagen.");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const autoTranslate = async () => {
    setTranslating(true); setMsg("");
    try {
      const doOne = async (text) => {
        if (!text || !text.trim()) return { en: "", fr: "" };
        const r = await fetch(`${API}/translate`, {
          method: "POST",
          headers: adminAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ text, source: "es", targets: ["en", "fr"] }),
        });
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        return d.translations || {};
      };
      const [tt, td] = await Promise.all([doOne(title.es), doOne(desc.es)]);
      setTitle((v) => ({ ...v, en: tt.en || v.en, fr: tt.fr || v.fr }));
      setDesc((v) => ({ ...v, en: td.en || v.en, fr: td.fr || v.fr }));
      setMsg("✓ Traducido desde ES (revisa y guarda).");
    } catch {
      setMsg("✗ La traducción automática falló.");
    }
    setTranslating(false);
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const tasks = [];
      if (imgDirty) tasks.push(onSaveImage(slots.image, imgUrl));
      if (titleDirty) tasks.push(onSaveText(slots.title, title));
      if (descDirty) tasks.push(onSaveText(slots.desc, desc));
      await Promise.all(tasks);
      setMsg("✓ Guardado y sincronizado en toda la web.");
    } catch {
      setMsg("✗ Error al guardar.");
    }
    setBusy(false);
  };

  return (
    <div data-testid={`admin-poi-card-${poiKey}-${index}`} className="bg-black/20 border border-white/10 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 flex-shrink-0 bg-black/40 overflow-hidden border border-white/10">
          {imgUrl
            ? <Img src={imgUrl} alt="" width={96} sizes="36px" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-3.5 h-3.5" /></div>}
        </div>
        <p className="text-[9px] tracking-[0.24em] uppercase text-[#D4A373] flex items-center gap-2">
          Card {index + 1}
          {overridden && <span className="text-[8px] bg-[#3E7C59]/30 text-[#7BB98A] px-1.5 py-0.5">editada</span>}
        </p>
        {dirty && <span className="w-2 h-2 rounded-full bg-[#D4A373]" title="Cambios sin guardar" />}
      </div>

      {/* Imagen */}
      <div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">Imagen</p>
        <div className="flex gap-2">
          <input
            value={imgUrl}
            onChange={(e) => setImgUrl(e.target.value)}
            placeholder="URL de la imagen (https://…)"
            data-testid={`admin-poi-image-${poiKey}-${index}`}
            className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373]"
          />
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            data-testid={`admin-poi-upload-${poiKey}-${index}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-white/70 hover:bg-white/5 disabled:opacity-40"
          >
            <UploadCloud className={`w-3.5 h-3.5 ${uploading ? "animate-pulse" : ""}`} strokeWidth={1.8} />
            {uploading ? "…" : "Subir"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
        </div>
      </div>

      {/* Título */}
      <div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">Título</p>
        <div className="space-y-1.5">
          {LANGS.map((l) => (
            <div key={l} className="flex items-center gap-2">
              <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 w-6">{l}</span>
              <input
                value={title[l]}
                onChange={(e) => setTitle((v) => ({ ...v, [l]: e.target.value }))}
                data-testid={`admin-poi-title-${poiKey}-${index}-${l}`}
                className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Descripción */}
      <div>
        <p className="text-[9px] tracking-[0.22em] uppercase text-white/45 mb-1.5">Descripción</p>
        <div className="space-y-1.5">
          {LANGS.map((l) => (
            <div key={l} className="flex items-start gap-2">
              <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 w-6 pt-1.5">{l}</span>
              <textarea
                value={desc[l]}
                onChange={(e) => setDesc((v) => ({ ...v, [l]: e.target.value }))}
                rows={2}
                data-testid={`admin-poi-desc-${poiKey}-${index}-${l}`}
                className="flex-1 bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-[#D4A373] resize-y"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={autoTranslate}
          disabled={translating || !title.es}
          data-testid={`admin-poi-translate-${poiKey}-${index}`}
          title="Rellena EN y FR a partir del español"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-white/70 hover:bg-white/5 disabled:opacity-40"
        >
          <Languages className={`w-3.5 h-3.5 ${translating ? "animate-pulse" : ""}`} strokeWidth={1.8} />
          {translating ? "Traduciendo…" : "Auto-traducir"}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || busy}
          data-testid={`admin-poi-save-${poiKey}-${index}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
            dirty && !busy ? "bg-[#C16542] hover:bg-[#A8533A] text-white" : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
        >
          <Save className="w-3 h-3" strokeWidth={2} /> {busy ? "Guardando…" : "Guardar"}
        </button>
      </div>
      {msg && (
        <p data-testid={`admin-poi-msg-${poiKey}-${index}`} className={`text-[11px] ${msg.startsWith("✗") ? "text-[#E07856]" : "text-[#7BB98A]"}`}>{msg}</p>
      )}
    </div>
  );
};


/* ============================================================
   Internal notification recipients. The unified CRM lives in LeadsPanel.
============================================================ */

const NotifyPanel = () => {
  const [emails, setEmails] = useState(null); // null = loading
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("xaluca_admin_token")}` });
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetch(`${API}/admin/notify-emails`, { headers: tokenHeader() });
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      setEmails(j.emails || []);
    } catch {
      setErr("No se pudo cargar la lista de destinatarios.");
      setEmails([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await fetch(`${API}/admin/notify-emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...tokenHeader() },
        body: JSON.stringify({ emails: next }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      setEmails(j.emails || []);
      setMsg("Cambios guardados.");
      setTimeout(() => setMsg(""), 3000);
    } catch {
      setErr("No se pudieron guardar los cambios.");
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const e = draft.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { setErr("Introduce un email válido."); return; }
    if ((emails || []).some((x) => x.toLowerCase() === e.toLowerCase())) { setErr("Ese email ya está en la lista."); return; }
    setDraft("");
    save([...(emails || []), e]);
  };
  const remove = (e) => save((emails || []).filter((x) => x !== e));

  return (
    <div data-testid="admin-notify-panel" className="p-6 md:p-10 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 text-[#D4A373]">
        <Mail className="w-5 h-5" strokeWidth={1.7} />
        <h2 className="text-base tracking-[0.18em] uppercase font-semibold text-white">Avisos de leads · Destinatarios</h2>
      </div>
      <p className="mt-3 text-sm text-white/60 leading-relaxed">
        Estos correos reciben un aviso cada vez que un cliente envía un formulario
        (contacto, planificador o descarga de programa). Los cambios se aplican al instante.
      </p>

      {/* Add form */}
      <div className="mt-7 flex gap-2">
        <input
          type="email"
          value={draft}
          data-testid="notify-email-input"
          onChange={(e) => { setDraft(e.target.value); setErr(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="nombre@xaluca.com"
          className="flex-1 bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white/90 outline-none focus:border-[#D4A373]"
        />
        <button
          onClick={add}
          disabled={busy || !draft.trim()}
          data-testid="notify-add-btn"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase transition-colors ${
            busy || !draft.trim() ? "bg-white/5 text-white/30 cursor-not-allowed" : "bg-[#C16542] hover:bg-[#A8533A] text-white"
          }`}
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Añadir
        </button>
      </div>

      {err && <p data-testid="notify-error" className="mt-3 text-xs text-[#E08A6A]">{err}</p>}
      {msg && <p data-testid="notify-msg" className="mt-3 text-xs text-[#7FB48F]">{msg}</p>}

      {/* List */}
      <ul className="mt-6 divide-y divide-white/10 border border-white/10">
        {emails === null && <li className="px-4 py-3 text-sm text-white/40">Cargando…</li>}
        {emails && emails.length === 0 && (
          <li className="px-4 py-3 text-sm text-white/40">No hay destinatarios. Añade al menos uno para recibir avisos.</li>
        )}
        {(emails || []).map((e) => (
          <li key={e} data-testid={`notify-row-${e}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-white/[0.03]">
            <span className="inline-flex items-center gap-2.5 text-sm text-white/90 min-w-0">
              <Mail className="w-3.5 h-3.5 text-[#D4A373] shrink-0" strokeWidth={1.6} />
              <span className="truncate">{e}</span>
            </span>
            <button
              onClick={() => remove(e)}
              disabled={busy}
              data-testid={`notify-remove-${e}`}
              title="Quitar destinatario"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-[#E08A6A] disabled:opacity-40 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} /> Quitar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
